import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Popover,
  InputAdornment,
  OutlinedInput
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ColorPicker from '../ColorPicker';
import categoryService from '../../services/categoryService';

const CategoriesPanel = ({ settings, categories, refreshCategories }) => {
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: '#808080'
  });

  // Color Picker Popover State
  const [colorAnchorEl, setColorAnchorEl] = useState(null);

  const handleColorClick = (event) => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchorEl(null);
  };

  const openColorPicker = Boolean(colorAnchorEl);
  const colorPickerId = openColorPicker ? 'simple-popover' : undefined;

  // Generic form change handler
  const handleCategoryChange = (field) => (event) => {
    setCategoryForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Category operations
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      color: '#808080'
    });
    setShowCategoryForm(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category._id);
    setCategoryForm({
      name: category.name,
      color: category.color
    });
    setShowCategoryForm(true);
  };

  const handleCancelCategory = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) {
      alert('Kategori adı gereklidir');
      return;
    }

    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory, categoryForm);
      } else {
        await categoryService.createCategory(settings._id, {
          ...categoryForm,
          timeline_id: settings._id
        });
      }

      setShowCategoryForm(false);
      await refreshCategories();

      // Dispatch custom event to notify timeline of category changes
      document.dispatchEvent(new CustomEvent('timelineDataUpdated'));
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Kategori kaydedilirken bir hata oluştu');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await categoryService.deleteCategory(categoryId);
      await refreshCategories();

      // Dispatch custom event to notify timeline of category changes
      document.dispatchEvent(new CustomEvent('timelineDataUpdated'));
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Kategori silinirken bir hata oluştu');
    }
  };

  return (
    <>
      {/* Button to add new category or form heading */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {showCategoryForm ?
            (editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori Ekle') :
            'Kategoriler'}
        </Typography>
        {!showCategoryForm && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddCategory}
            startIcon={<EditIcon />}
          >
            Kategori Ekle
          </Button>
        )}
      </Box>

      {/* Category form - shown only when adding or editing */}
      {showCategoryForm ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Kategori Adı"
              fullWidth
              margin="normal"
              value={categoryForm.name}
              onChange={handleCategoryChange('name')}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Kategori Rengi
              </Typography>
              <Box
                onClick={handleColorClick}
                sx={{
                  width: '100%',
                  cursor: 'pointer',
                  position: 'relative'
                }}
              >
                <TextField
                  fullWidth
                  value={categoryForm.color}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: categoryForm.color,
                            border: '1px solid rgba(0,0,0,0.1)',
                            boxShadow: '0 0 0 1px rgba(255,255,255,0.5) inset'
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <KeyboardArrowDownIcon color="action" />
                      </InputAdornment>
                    ),
                    style: { cursor: 'pointer' }
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      cursor: 'pointer'
                    },
                    '& .MuiInputBase-input': {
                      cursor: 'pointer'
                    }
                  }}
                />
              </Box>
              <Popover
                id={colorPickerId}
                open={openColorPicker}
                anchorEl={colorAnchorEl}
                onClose={handleColorClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <ColorPicker
                  color={categoryForm.color}
                  onChange={(newColor) => {
                    handleCategoryChange('color')({ target: { value: newColor } });
                    // Optional: Close on select if desired, but user asked for "click outside to revert" (which usually means close).
                    // Actually user said "mouse ile bir yere tıklayınca eski haline geri alsın" -> click elsewhere to close.
                    // But usually picking a color should also close it for better UX? 
                    // I will keep it open to allow browsing, as "click elsewhere to close" is the specific request.
                    // Wait, "eski haline geri alsın" might mean "revert to original state" (closed).
                    // I'll stick to standard behavior: click outside closes.
                  }}
                />
              </Popover>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancelCategory}
                sx={{ mr: 1 }}
              >
                İptal
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveCategory}
              >
                {editingCategory ? 'Kategoriyi Güncelle' : 'Kategori Ekle'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      ) : (
        /* List of existing categories - shown when not adding/editing */
        <Box>
          {categories.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              Henüz kategori eklenmemiş
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              {categories.map((category) => (
                <Box
                  key={category._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    boxShadow: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: category.color,
                        mr: 2
                      }}
                    />
                    <Typography>{category.name}</Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditCategory(category)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCategory(category._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default CategoriesPanel;